import { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { addDatasource, removeDatasource } from '../../../utils/demoEnvMgmtApi';
import { errorMessage } from './error-message';
import { formatProjectLabel, formatDatasourceLabel } from '../label-format';
import ConfirmDeleteDialog from '../../../components/admin/ConfirmDeleteDialog';

function DatasourcesSection({ datasources, projects, sourceDatasources, onChanged }) {
  const [datasourceId, setDatasourceId] = useState('');
  const [label, setLabel] = useState('');
  const [kind, setKind] = useState('source');
  const [demoEnvProjectId, setDemoEnvProjectId] = useState('');
  const [linkedSourceDatasourceId, setLinkedSourceDatasourceId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const hasProjects = projects.length > 0;

  const handleKindChange = (nextKind) => {
    setKind(nextKind);
    // The project applies to both kinds (source and target datasources can live under different
    // projects) — only the target-only source-link field resets.
    if (nextKind === 'source') {
      setLinkedSourceDatasourceId('');
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!datasourceId.trim() || !label.trim()) return;
    // Required for both kinds — every datasource must belong to exactly one project.
    if (!demoEnvProjectId) {
      setError('Select a project for this datasource — add a project first if none exist yet.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await addDatasource({
        datasourceId: Number(datasourceId),
        label: label.trim(),
        kind,
        demoEnvProjectId: Number(demoEnvProjectId),
        sourceDatasourceId: kind === 'target' ? Number(linkedSourceDatasourceId) : undefined,
      });
      setDatasourceId('');
      setLabel('');
      setDemoEnvProjectId('');
      setLinkedSourceDatasourceId('');
      await onChanged();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteConfirm = async () => {
    setError(null);
    setSubmitting(true);
    try {
      await removeDatasource(deleteTarget.id);
      setDeleteTarget(null);
      await onChanged();
    } catch (err) {
      setError(errorMessage(err));
      setDeleteTarget(null);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="demo-env-mgmt-section">
      <h2 className="demo-env-mgmt-section-title">[L4] Datasources</h2>
      <p className="demo-env-mgmt-label">
        SOURCE datasources are read-only. TARGET datasources are the ones actually
        cleared/re-created. Every datasource — source or target — must belong to exactly one
        Project above; its login is resolved from that project's linked account, not set here.
      </p>

      <div className="demo-env-mgmt-columns" style={{ marginTop: 8 }}>
        <div className="demo-env-mgmt-column">
          <h3 className="demo-env-mgmt-column-title">Add datasource</h3>
          {!hasProjects && (
            <p className="demo-env-mgmt-label" style={{ marginBottom: 6 }}>
              Add a project above before adding a datasource — every datasource, source or
              target, must always be linked to one.
            </p>
          )}
          <form onSubmit={handleAdd}>
            <div className="demo-env-mgmt-field">
              <label className="demo-env-mgmt-label" htmlFor="new-datasource-id">Datasource id</label>
              <input
                id="new-datasource-id"
                className="demo-env-mgmt-input"
                type="number"
                min="1"
                value={datasourceId}
                onChange={(e) => setDatasourceId(e.target.value)}
                required
              />
            </div>
            <div className="demo-env-mgmt-field" style={{ marginTop: 8 }}>
              <label className="demo-env-mgmt-label" htmlFor="new-datasource-label">Label</label>
              <input
                id="new-datasource-label"
                className="demo-env-mgmt-input"
                type="text"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                required
              />
            </div>
            <div className="demo-env-mgmt-field" style={{ marginTop: 8 }}>
              <label className="demo-env-mgmt-label" htmlFor="new-datasource-kind">Type</label>
              <select
                id="new-datasource-kind"
                className="demo-env-mgmt-select"
                value={kind}
                onChange={(e) => handleKindChange(e.target.value)}
              >
                <option value="source" disabled={!hasProjects}>
                  Source{!hasProjects ? ' (add a project first)' : ''}
                </option>
                <option value="target" disabled={!hasProjects}>
                  Target{!hasProjects ? ' (add a project first)' : ''}
                </option>
              </select>
            </div>
            <div className="demo-env-mgmt-field" style={{ marginTop: 8 }}>
              <label className="demo-env-mgmt-label" htmlFor="new-datasource-project">[L2] Project</label>
              <select
                id="new-datasource-project"
                className="demo-env-mgmt-select"
                value={demoEnvProjectId}
                onChange={(e) => setDemoEnvProjectId(e.target.value)}
                required
              >
                <option value="">Select a project…</option>
                {projects.map((project) => (
                  <option key={project.id} value={String(project.id)}>{formatProjectLabel(project)} ({project.projectId})</option>
                ))}
              </select>
            </div>
            {kind === 'target' && (
              <div className="demo-env-mgmt-field" style={{ marginTop: 8 }}>
                <label className="demo-env-mgmt-label" htmlFor="new-datasource-source">[L4] Source datasource</label>
                <select
                  id="new-datasource-source"
                  className="demo-env-mgmt-select"
                  value={linkedSourceDatasourceId}
                  onChange={(e) => setLinkedSourceDatasourceId(e.target.value)}
                  required
                >
                  <option value="">{sourceDatasources.length === 0 ? 'No source datasources yet' : 'Select a source datasource…'}</option>
                  {sourceDatasources.map((ds) => (
                    <option key={ds.id} value={String(ds.id)}>{formatDatasourceLabel(ds)} ({ds.datasourceId})</option>
                  ))}
                </select>
              </div>
            )}
            <div className="demo-env-mgmt-field demo-env-mgmt-field-actions" style={{ marginTop: 8 }}>
              <button
                className="demo-env-mgmt-btn"
                type="submit"
                disabled={submitting || !hasProjects}
              >
                {submitting ? 'Adding…' : 'Add'}
              </button>
            </div>
          </form>
          {error && <p className="demo-env-mgmt-error">{error}</p>}
        </div>

        <div className="demo-env-mgmt-column">
          <h3 className="demo-env-mgmt-column-title">[L4] Datasources</h3>
          <div className="demo-env-mgmt-table-wrap">
            <table className="demo-env-mgmt-table">
              <thead>
                <tr>
                  <th>Datasource id</th>
                  <th>Label</th>
                  <th>Type</th>
                  <th>Project</th>
                  <th className="demo-env-mgmt-table-actions" />
                </tr>
              </thead>
              <tbody>
                {datasources.map((datasource) => (
                  <tr key={datasource.id}>
                    <td>
                      <a href={`https://app.contentsquare.com/#/home?project=${datasource.datasourceId}`} target="_blank" rel="noopener noreferrer">
                        {datasource.datasourceId}
                      </a>
                    </td>
                    <td>{datasource.label}</td>
                    <td>{datasource.kind.toUpperCase()}</td>
                    <td>{datasource.project ? formatProjectLabel(datasource.project) : '—'}</td>
                    <td className="demo-env-mgmt-table-actions">
                      <button
                        type="button"
                        className="demo-env-mgmt-btn-delete"
                        onClick={() => setDeleteTarget(datasource)}
                        disabled={submitting}
                      >
                        <Trash2 size={13} /> Delete
                      </button>
                    </td>
                  </tr>
                ))}
                {datasources.length === 0 && (
                  <tr className="demo-env-mgmt-empty-row">
                    <td colSpan={5}>No datasources yet.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {deleteTarget && (
        <ConfirmDeleteDialog
          entityType="datasource"
          entityName={formatDatasourceLabel(deleteTarget)}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}

export default DatasourcesSection;
